"use client";
import React from "react";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children?: React.ReactNode;
  clickable?: boolean;
  className?: string;
}

const Row = React.forwardRef<HTMLTableRowElement, RowProps>(function Row(
  { children, clickable = false, className, ...otherProps }: RowProps,
  ref
) {
  return (
    <tr
      className={SubframeUtils.twClassNames(
        "group/5d119f8d border-t border-solid border-neutral-border",
        { "hover:bg-neutral-50": clickable },
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {children}
    </tr>
  );
});

interface CellProps extends React.TdHTMLAttributes<HTMLTableDataCellElement> {
  children?: React.ReactNode;
  className?: string;
}

const Cell = React.forwardRef<HTMLDivElement, CellProps>(function Cell(
  { children, className, ...otherProps }: CellProps,
  ref
) {
  const widthClasses = className
    ?.split(" ")
    .filter(
      (c) =>
        c.startsWith("w-") || c.startsWith("min-w-") || c.startsWith("max-w-")
    )
    .join(" ");

  const innerClasses = className
    ?.split(" ")
    .filter(
      (c) =>
        !c.startsWith("w-") &&
        !c.startsWith("min-w-") &&
        !c.startsWith("max-w-")
    )
    .join(" ");

  return (
    <td className={widthClasses} {...otherProps}>
      <div
        className={SubframeUtils.twClassNames(
          "flex h-12 items-center gap-1 px-3 min-w-0",
          innerClasses
        )}
        ref={ref}
      >
        {children}
      </div>
    </td>
  );
});

interface HeaderRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children?: React.ReactNode;
  className?: string;
}

const HeaderRow = React.forwardRef<HTMLTableRowElement, HeaderRowProps>(
  function HeaderRow(
    { children, className, ...otherProps }: HeaderRowProps,
    ref
  ) {
    return (
      <tr className={className} ref={ref} {...otherProps}>
        {children}
      </tr>
    );
  }
);

interface HeaderCellProps
  extends React.ThHTMLAttributes<HTMLTableHeaderCellElement> {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}

const HeaderCell = React.forwardRef<HTMLDivElement, HeaderCellProps>(
  function HeaderCell(
    { children, icon = null, className, ...otherProps }: HeaderCellProps,
    ref
  ) {
    const widthClasses = className
      ?.split(" ")
      .filter(
        (c) =>
          c.startsWith("w-") || c.startsWith("min-w-") || c.startsWith("max-w-")
      )
      .join(" ");

    const innerClasses = className
      ?.split(" ")
      .filter(
        (c) =>
          !c.startsWith("w-") &&
          !c.startsWith("min-w-") &&
          !c.startsWith("max-w-")
      )
      .join(" ");

    return (
      <th className={widthClasses} {...otherProps}>
        <div
          className={SubframeUtils.twClassNames(
            "flex h-8 items-center gap-1 px-3 min-w-0",
            innerClasses
          )}
          ref={ref}
        >
          {children ? (
            <span className="whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color truncate">
              {children}
            </span>
          ) : null}
          {icon ? (
            <SubframeCore.IconWrapper className="text-caption font-caption text-subtext-color">
              {icon}
            </SubframeCore.IconWrapper>
          ) : null}
        </div>
      </th>
    );
  }
);

interface TableRootProps extends React.TableHTMLAttributes<HTMLTableElement> {
  header?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const TableRoot = React.forwardRef<HTMLTableElement, TableRootProps>(
  function TableRoot(
    { header, children, className, ...otherProps }: TableRootProps,
    ref
  ) {
    return (
      <table
        className={SubframeUtils.twClassNames("w-full", className)}
        ref={ref}
        {...otherProps}
      >
        <thead>{header}</thead>
        <tbody className="border-b border-solid border-neutral-border">
          {children}
        </tbody>
      </table>
    );
  }
);

export const Table = Object.assign(TableRoot, {
  Row,
  Cell,
  HeaderRow,
  HeaderCell,
});

